import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../../../components/common/Modal';
import { Input } from '../../../../components/common/Input';
import { Button } from '../../../../components/common/Button';
import type { PricingPlan } from '../../data/pricingPlans';

interface CheckoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    plan: PricingPlan;
    initialMode: 'start' | 'sales';
}

type Step = 'initial' | 'sales_choice' | 'extended_registration';

export function CheckoutModal({ isOpen, onClose, plan, initialMode }: CheckoutModalProps) {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('initial');
    const [formData, setFormData] = useState({
        cnpj: '',
        name: '',
        email: '',
        password: '',
        whatsapp: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleStartNowSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // In a real app, you might validate or pre-register here
        navigate('/checkout', { state: { plan, user: formData } });
    };

    const handleSalesInitialSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.cnpj) {
            setStep('sales_choice');
        }
    };

    const handleHireWithDiscount = () => {
        setStep('extended_registration');
    };

    const handleTalkToSales = () => {
        // Logic to open WhatsApp or sales chat
        window.open(`https://wa.me/5511999999999?text=Olá, tenho interesse no plano ${plan.name} e meu CNPJ é ${formData.cnpj}`, '_blank');
        onClose();
    };

    const handleExtendedSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        navigate('/checkout', { state: { plan, user: formData, discount: true } });
    };

    const resetAndClose = () => {
        setStep('initial');
        setFormData({ cnpj: '', name: '', email: '', password: '', whatsapp: '' });
        onClose();
    };

    // Title logic
    const getTitle = () => {
        if (initialMode === 'start') return 'Crie sua conta';
        if (step === 'initial') return 'Fale com nosso time';
        if (step === 'sales_choice') return 'O que prefere?';
        if (step === 'extended_registration') return 'Dados para contrato';
        return '';
    };

    return (
        <Modal isOpen={isOpen} onClose={resetAndClose} title={getTitle()}>
            {/* FLOW: START NOW (Free/Direct) */}
            {initialMode === 'start' && (
                <form onSubmit={handleStartNowSubmit} className="space-y-4">
                    <Input
                        label="CNPJ"
                        name="cnpj"
                        placeholder="00.000.000/0000-00"
                        value={formData.cnpj}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label="Nome do Contato"
                        name="name"
                        placeholder="Seu nome completo"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label="Email Profissional"
                        name="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label="Senha"
                        name="password"
                        type="password"
                        placeholder="Crie uma senha segura"
                        value={formData.password}
                        onChange={handleChange}
                        required
                    />
                    <div className="pt-4">
                        <Button type="submit" className="w-full">
                            Continuar para Pagamento
                        </Button>
                    </div>
                </form>
            )}

            {/* FLOW: SALES (Enterprise/High Volume) */}
            {initialMode === 'sales' && step === 'initial' && (
                <form onSubmit={handleSalesInitialSubmit} className="space-y-4">
                    <p className="text-gray-600 mb-4">
                        Para melhor atendê-lo, por favor informe o CNPJ da sua empresa.
                    </p>
                    <Input
                        label="CNPJ"
                        name="cnpj"
                        placeholder="00.000.000/0000-00"
                        value={formData.cnpj}
                        onChange={handleChange}
                        required
                    />
                    <div className="pt-4">
                        <Button type="submit" className="w-full">
                            Continuar
                        </Button>
                    </div>
                </form>
            )}

            {initialMode === 'sales' && step === 'sales_choice' && (
                <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                        <p className="text-green-800 font-medium text-center">
                            Temos uma oferta especial para você!
                        </p>
                    </div>

                    <Button
                        onClick={handleTalkToSales}
                        variant="secondary"
                        className="w-full"
                    >
                        Falar com Vendas
                    </Button>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500">OU</span>
                        </div>
                    </div>

                    <Button
                        onClick={handleHireWithDiscount}
                        className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-lg shadow-green-500/30"
                    >
                        Contratar agora com 10% OFF
                    </Button>
                </div>
            )}

            {initialMode === 'sales' && step === 'extended_registration' && (
                <form onSubmit={handleExtendedSubmit} className="space-y-4">
                    <Input
                        label="Nome do Contato"
                        name="name"
                        placeholder="Seu nome completo"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label="Email Profissional"
                        name="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label="WhatsApp"
                        name="whatsapp"
                        placeholder="(11) 99999-9999"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label="Senha"
                        name="password"
                        type="password"
                        placeholder="Crie uma senha segura"
                        value={formData.password}
                        onChange={handleChange}
                        required
                    />
                    <div className="pt-4">
                        <Button type="submit" className="w-full">
                            Finalizar Contratação
                        </Button>
                    </div>
                </form>
            )}
        </Modal>
    );
}
